// --- Configurable knockout chart constants ---

// Time window
export const SECONDS_SHOWN = 30;

// Polling
export const DATA_INTERVAL_MS = 1000; // How often real price data arrives (S3 updates once per second)

/**
 * Deliberately faster than the publish rate, and not to be "matched" to it.
 *
 * Polling on the same period as the source does not stay in phase with it: the two drift, so a tick can be
 * read up to two intervals after it was published. The playhead trails `BUFFER_BEHIND` behind the newest
 * sample, so it gets fed unevenly and the line stutters. At half the period the worst case is roughly one
 * interval instead of two.
 *
 * Shortening the timer only works alongside a revalidating fetch. The source is served with `max-age=1`, so
 * under the default cache mode the second poll of each second is answered from the HTTP cache with the bytes
 * it already had: the timer speeds up while the data does not. `queries/externalSource` therefore fetches
 * with `cache: 'no-cache'`, which the origin answers with a 304 and no body when the file has not changed.
 *
 * Background polling is separately disabled (`refetchIntervalInBackground: false` in `queries/externalSource`),
 * which is where the polling cost actually was.
 */
export const POLL_INTERVAL_MS = DATA_INTERVAL_MS / 2;

// Animation
export const FRAME_RATE = 30;
export const FRAME_INTERVAL_MS = Math.floor(1000 / FRAME_RATE);

// Y-domain auto-scaling
export const Y_DOMAIN_PADDING = 0.15; // Breathing room above/below data (fraction of range)
export const Y_DOMAIN_MIN_RANGE_FLOOR = 0.000001; // Absolute minimum range ($) to prevent zero-range
export const Y_AXIS_NUM_TICKS = 5;

// Buffer: stay N seconds behind real-time so the playhead always interpolates between known S3 values
export const BUFFER_BEHIND = 4;

/**
 * How far behind the clock the single-asset live knockout chart draws. Fixed, so the line glides at the clock's
 * speed. Its live series gains a second roughly every second and is 1-2s old when it lands, so 3s keeps the
 * playhead between known samples almost all of the time.
 */
export const LIVE_PLAYHEAD_BEHIND_MS = 3_000;

/**
 * How long the playhead may sit at the newest sample before the chart pins its window and reports the feed as
 * delayed. Shorter waits keep scrolling on the clock, with the line ending at its last real price until the next
 * second lands.
 */
export const LIVE_FEED_GAP_PIN_MS = 3_000;

// X-axis ticks
export const X_TICK_INTERVAL_MS = 5_000; // 5-second intervals (desktop)
export const X_TICK_INTERVAL_MS_MOBILE = 10_000; // 10-second intervals (mobile)

// Curve interpolation
export const KNOCKOUT_CURVE_TYPE = 'monotoneX' as const;

/**
 * Curve for the wider 5m/15m/1h grains only. The live 30s view keeps `KNOCKOUT_CURVE_TYPE`.
 *
 * Straight segments between points. At ~300 points across the chart the samples sit a few pixels apart, so
 * plain interpolation reads as a smooth continuous line while showing the data exactly as it is.
 *
 * Curve smoothing actively hurts at this density. `bumpX` (the app default elsewhere) places a horizontal
 * tangent at every point, which at a few pixels apart becomes a flat spot per sample with a fast transition
 * between, i.e. it draws steps. `monotoneX` avoids the flats but bends through each sample, so it renders
 * every wiggle. Both are built for sparse series; these grains are dense, so no interpolation beats either.
 *
 * The live view keeps `monotoneX`: it animates a buffered playhead over ~40 raw 1s points, a genuinely sparse
 * series where tracking the data tightly is the point.
 */
export const KNOCKOUT_GRAIN_CURVE_TYPE = 'linear' as const;

// Current price line
export const SHOW_CURRENT_PRICE_LINE = true;

/**
 * Crypto asset brand colors for the knockout chart line + area fill.
 *
 * Per-theme, like the rest of the design system: the chart paints on `#FFFFFF` in light and `#0A0C0F` in dark
 * (and the mobile embed defaults to dark), so a single brand hex is guaranteed to be wrong in one of them.
 * NEAR is the case that forced this: its brand color is black, which is correct on white and invisible on the
 * dark background. Most assets read fine either way and simply repeat the same value.
 */
export interface CryptoAssetColor {
    light: string;
    dark: string;
}

export const KNOCKOUT_ASSET_COLORS: Record<string, CryptoAssetColor> = {
    BTC: { light: '#F7931A', dark: '#F7931A' },
    ETH: { light: '#627EEA', dark: '#627EEA' },
    SOL: { light: '#9945FF', dark: '#9945FF' },
    XRP: { light: '#00AAE4', dark: '#00AAE4' },
    DOGE: { light: '#C2A633', dark: '#C2A633' },
    SHIBA: { light: '#FFA409', dark: '#FFA409' },
    BNB: { light: '#F3BA2F', dark: '#F3BA2F' },
    HYPE: { light: '#00E5C4', dark: '#00E5C4' },
    ZEC: { light: '#F3B724', dark: '#F3B724' },
    // Brand black on white; brand green on dark, where black is ~1.03:1 against `#0A0C0F` (i.e. invisible).
    NEAR: { light: '#111111', dark: '#00EC97' },
    // Commodities have no brand colour the way a token does, so these are picked for the metal/crude
    // association while staying legible in both themes. Each takes a deeper shade on light and a lighter one
    // on dark, since the natural association (pale silver, near-black crude) is invisible on one side.
    GOLD: { light: '#B8860B', dark: '#E8B923' },
    SILVER: { light: '#6B7280', dark: '#CBD5E1' },
    WTI: { light: '#8B5E34', dark: '#D89B5A' },
    PALLADIUM: { light: '#64748B', dark: '#CBD5E1' },
    PLATINUM: { light: '#52606D', dark: '#D9E2EC' },
    COPPER: { light: '#B45309', dark: '#F59E0B' },
    NATGAS: { light: '#2563EB', dark: '#60A5FA' },
    // Match the FX accents in app_casual/feature_flags.json.
    EURUSD: { light: '#4D73EE', dark: '#4D73EE' },
    GBPUSD: { light: '#00A600', dark: '#00A600' },
    USDJPY: { light: '#00A600', dark: '#00A600' },
};

// Falls back to `undefined` for an unknown asset; callers use a theme palette colour, so a missing entry
// renders a visible line rather than nothing.
export const getKnockoutAssetColor = (asset: string | undefined, isDarkMode: boolean): string | undefined => {
    const color = asset ? KNOCKOUT_ASSET_COLORS[asset] : undefined;
    if (!color) return undefined;
    return isDarkMode ? color.dark : color.light;
};

/**
 * CF Benchmarks index ID per crypto asset, for the `cfbenchmarks_value` WS channel.
 *
 * Mirrors the exchange's own `coinToIndex` map in
 * svc-external-ingestion/adapters/cfbenchmarks. Bitcoin is the odd one out (`BRTI`); the rest
 * follow `<COIN>USD_RTI`, with SHIBA shortened to SHIB.
 *
 * The channel's `avg_60s_data` is the same 60-second trailing average the public-docs
 * `timeseries` carries (verified against both lanes: it matches the S3 series to ~1e-6 while
 * raw spot differs by dollars) and the same quantity these markets resolve on, so a tick from
 * this channel is a drop-in for a polled sample rather than a different measurement.
 */
export const CF_BENCHMARKS_INDEX_BY_ASSET: Record<string, string> = {
    BTC: 'BRTI',
    ETH: 'ETHUSD_RTI',
    SOL: 'SOLUSD_RTI',
    XRP: 'XRPUSD_RTI',
    DOGE: 'DOGEUSD_RTI',
    SHIBA: 'SHIBUSD_RTI',
    BNB: 'BNBUSD_RTI',
    HYPE: 'HYPEUSD_RTI',
    ZEC: 'ZECUSD_RTI',
    NEAR: 'NEARUSD_RTI',
};

export const ASSET_BY_CF_BENCHMARKS_INDEX: Record<string, string> = Object.fromEntries(
    Object.entries(CF_BENCHMARKS_INDEX_BY_ASSET).map(([asset, indexId]) => [indexId, asset]),
);
